import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SuperadminDashboard from './SuperadminDashboard';
import GerenteDashboard from './GerenteDashboard';
import FornecedorDashboard from './FornecedorDashboard';
import RecebedorDashboard from './RecebedorDashboard';
import QualidadeDashboard from './QualidadeDashboard';
import AdministradorDashboard from './AdministradorDashboard';
import EquipamentosPainelPage from './equipamentos/EquipamentosPainelPage';
import FornecedorEquipamentosPage from './equipamentos/FornecedorEquipamentosPage';
import { Loader2, AlertTriangle, Shield } from 'lucide-react';

const Dashboard = () => {
  const { user, userData, loading, hasPermission } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Usuário não encontrado</h3>
          <p className="text-gray-600">Faça login para acessar o dashboard.</p>
        </div>
      </div>
    );
  }

  // Verificar se o usuário tem permissão para visualizar dashboard
  if (!hasPermission('view_dashboard')) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Acesso Negado</h3>
          <p className="text-gray-600">Você não tem permissão para visualizar o dashboard.</p>
        </div>
      </div>
    );
  }

  // Log para debug
  console.log('🔍 Dashboard: Renderizando para usuário:', {
    funcao: userData.funcao,
    nome: userData.nome,
    empresa: userData.empresa?.nome
  });

  // Renderizar dashboard baseado na função do usuário
  switch (userData.funcao) {
    case 'superadmin':
      console.log('📊 Dashboard: Renderizando SuperadminDashboard');
      return <SuperadminDashboard />;
      
    case 'gerente':
      console.log('📊 Dashboard: Renderizando GerenteDashboard');
      return <GerenteDashboard />;
      
    case 'administrador':
    case 'admin': // ✅ CORREÇÃO: Adicionar suporte para 'admin'
      console.log('📊 Dashboard: Renderizando AdministradorDashboard');
      return <AdministradorDashboard />;
      
    case 'recebedor':
      console.log('📊 Dashboard: Renderizando RecebedorDashboard');
      return <RecebedorDashboard />;
      
    case 'qualidade':
      console.log('📊 Dashboard: Renderizando QualidadeDashboard');
      return <QualidadeDashboard />;
      
    case 'fornecedor':
      console.log('📊 Dashboard: Renderizando FornecedorDashboard');
      return <FornecedorDashboard />;

    case 'producao':
      console.log('📊 Dashboard: Redirecionando produção para a estação de trabalho');
      return <Navigate to="/app/trabalho" replace />;

    case 'manutencao':
      return <EquipamentosPainelPage />;

    case 'fornecedor_equipamentos':
      return <FornecedorEquipamentosPage />;
      
    case 'usuario':
      // Usuário básico - mostrar dashboard simplificado
      console.log('📊 Dashboard: Renderizando dashboard básico para usuário');
      return (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Bem-vindo, {userData.nome}!
            </h1>
            <p className="text-gray-600 mb-4">
              Você está logado como <strong>{userData.funcao}</strong> na empresa <strong>{userData.empresa?.nome}</strong>.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center">
                <Shield className="h-5 w-5 text-blue-500 mr-2" />
                <div>
                  <h3 className="text-sm font-medium text-blue-900">Acesso Limitado</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    Seu perfil tem acesso limitado ao sistema. Entre em contato com o administrador para solicitar permissões adicionais.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
      
    default:
      console.log('❌ Dashboard: Função não reconhecida:', userData.funcao);
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Função não reconhecida</h3>
            <p className="text-gray-600 mb-4">
              Função de usuário não reconhecida: <strong>{userData.funcao}</strong>
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-left">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Informações do usuário:</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li><strong>Nome:</strong> {userData.nome}</li>
                <li><strong>Email:</strong> {userData.email}</li>
                <li><strong>Função:</strong> {userData.funcao}</li>
                <li><strong>Empresa:</strong> {userData.empresa?.nome}</li>
                <li><strong>Status:</strong> {userData.status}</li>
              </ul>
            </div>
            <p className="text-sm text-gray-500 mt-4">
              Entre em contato com o administrador do sistema para corrigir sua função de usuário.
            </p>
          </div>
        </div>
      );
  }
};

export default Dashboard;

